export interface LayoutItem {
  /** Component key */
  key: string;

  /** X coordinate measured in grid units */
  x: number;

  /** Y coordinate measured in grid units */
  y: number;

  /** Width measured in grid units or a ratio of visible grid columns */
  w: number;
  /** Height measured in grid units or a ratio of visible grid rows */
  h: number;

  /** Minimum width measured in grid units or a ratio of visible grid columns */
  minW?: number;

  /** Maximum width measured in grid units or a ratio of visible grid columns */
  maxW?: number;

  /** Minimum height measured in grid units or a ratio of visible grid rows */
  minH?: number;

  /** Maximum height measured in grid units or a ratio of visible grid rows */
  maxH?: number;

  /** If an item is static, it is not movable by either user interaction or compacting events */
  static?: boolean;
}

export interface LayoutDetails {
  rowHeight: number;
  cols: number;
  columnWidth: number;
  containerWidth: number;
  containerPadding: { x: number; y: number };
  margin: { x: number; y: number };
}

export interface LayoutItemOffset {
  x: number;
  y: number;
}

export type ResponsiveMap<T extends { [key: string]: number }, V> = {
  [key in keyof T]: V;
};

export interface Position {
  left: number;
  top: number;
  width: number;
  height: number;
}

// TODO: In future it may make sense to allow the developer to customise this
//       rather than relying on a complex default.
//       The use of sm/md/lg/xs/xxs/xxl... may also be preferred

export enum ResponsiveBreakpoints {
  Mobile = 'Mobile', // 320-480px, grid is 2
  Tablets = 'Tablets', // 600-767px, grid is 4
  Laptop = 'Laptop', // 768-1023px, grid is 6
  LargeLaptop = 'LargeLaptop', // 1024-1365, grid is 8
  Desktop = 'Desktop', // 1366-1919px, grid is 10
  LargeDesktop = 'LargeDesktop', // 1920-2559px, grid is 12
  TV = 'TV', // 3840px and up
}

export type Layout = ReadonlyArray<LayoutItem>;

export const Breakpoints: { [key in ResponsiveBreakpoints]: number } = {
  Mobile: 320,
  Tablets: 600,
  Laptop: 768,
  LargeLaptop: 1024,
  Desktop: 1366,
  LargeDesktop: 1920,
  TV: 3840,
};

export type ResponsiveLayout = {
  [key in ResponsiveBreakpoints]: ReadonlyArray<LayoutItem>;
};

export enum CompactType {
  None,
  Horizontal = 1 << 1,
  Vertical = 1 << 2,
  /**
   * This only effects vertical (since horizontal doesn't have the ability to not preserve visual order)
   * there are some cases of compaction (below) that can cause the visual order of elements (top to bottom, left to right)
   * to become different, this may not be preferred and thus is controlled through this optional flag.
   * |---A---|
   *      |-B-|
   * <blank visual space>
   * |-C-|
   * As you can see above C can compact vertically to make it like below, however what has now occurred
   * is that C is now visually before B, where as before it was after.
   * |---A---|
   * |-C-||-B-|
   * Enabling this flag, will cause C to compact vertically but not to the point where it would visually
   * occur before B.  That is
   * |---A---|
   *      |-B-|
   * |-C-|
   */
  PreservingVisualOrder = 1 << 3,
}
